import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Dropdown, Message } from 'semantic-ui-react';
import { useUtilityMeters } from '../../../providers/UtilityMeterProvider';

const formStyle = {
    field: {
        display: 'flex',
        alignItems: 'center',
    },
    label: {
        fontSize: '1.2rem',
        marginRight: '1rem',
        width: '40%',
    },
    input: {
        width: '100%',
    },
};

const meterTypeOptions = [
    { key: 'electric', text: 'Electric', value: 'electric' },
    { key: 'gas', text: 'Gas', value: 'gas' },
];

export const UtilityMeterModal = ({
    open,
    setOpen,
    type,
    utilityAccountId,
    selectedMeter,
    onSuccess,
}) => {
    const [name, setName] = useState('');
    const [meterType, setMeterType] = useState('');
    const [meterId, setMeterId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);

    const { createUtilityMeter, updateUtilityMeter } = useUtilityMeters();

    const isCreate = type === 'Create';

    const clearForm = () => {
        setName('');
        setMeterType('');
        setMeterId('');
        setErrors([]);
    };

    useEffect(() => {
        if (type === 'Edit' && selectedMeter) {
            setName(selectedMeter.name ?? '');
            setMeterType(selectedMeter.type ?? '');
            setMeterId(selectedMeter.meterId ?? '');
        } else {
            clearForm();
        }
    }, [selectedMeter, type, open]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setErrors(['Name is required.']);
            return;
        } else if (!meterType) {
            setErrors(['Type is required.']);
            return;
        } else if (!meterId.trim()) {
            setErrors(['Meter ID is required.']);
            return;
        }

        setIsSubmitting(true);
        setErrors([]);

        try {
            const payload = {
                name: name.trim(),
                type: meterType,
                meterId: meterId.trim(),
            };

            if (isCreate) {
                await createUtilityMeter(utilityAccountId, payload);
            } else {
                await updateUtilityMeter(selectedMeter.id, payload);
            }

            await onSuccess();
            setOpen(false);
        } catch (error) {
            console.error('Failed to save utility meter:', error);
            setErrors([error.message || 'Failed to save utility meter. Please try again.']);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            size="tiny"
            closeIcon={!isSubmitting}
            open={open}
            onClose={() => {
                if (isSubmitting) return;
                setOpen(false);
                clearForm();
            }}
            onOpen={() => setOpen(true)}
            style={{ textAlign: 'left' }}
        >
            <Modal.Header>{isCreate ? 'Create' : 'Edit'} Utility Meter</Modal.Header>
            <Modal.Content>
                <Form error={errors.length > 0} loading={isSubmitting}>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Name</label>
                        <input
                            placeholder="Name"
                            style={formStyle.input}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Type</label>
                        <Dropdown
                            placeholder="Type"
                            options={meterTypeOptions}
                            value={meterType}
                            onChange={(e, { value }) => setMeterType(value)}
                            disabled={isSubmitting}
                            selection
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Meter ID</label>
                        <input
                            placeholder="Meter ID"
                            style={formStyle.input}
                            value={meterId}
                            onChange={e => setMeterId(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </Form.Field>
                    <div className="error-container">
                        <Message error list={errors} />
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={() => {
                        setOpen(false);
                        clearForm();
                    }}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    className="ui primary button"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                >
                    {isCreate ? 'Submit' : 'Save'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
